import { useState } from 'react'
import {
  Collapse, Group, Text, Badge, Stack, Anchor,
  Skeleton, Alert, ActionIcon, Divider, Box,
} from '@mantine/core'
import { IconChevronDown, IconChevronUp, IconAlertCircle, IconServer } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'

interface ResourceState {
  status?: string
  cpu_pct?: number
  disk_pct?: number
  uptime_hours?: number
}

interface AuditEvent {
  action: string
  actor: string
  at: string
}

interface Alert_ {
  type: string
  message: string
}

interface InfraContext {
  resource_state: ResourceState | null
  recent_audit_events: AuditEvent[]
  active_alerts: Alert_[]
}

interface InfraContextPanelProps {
  taskRequestId: number
  tenantId: number | string | undefined
  resourceType: string | null
  resourceId: string | null
  resourceName: string | null
}

export function InfraContextPanel({
  taskRequestId, tenantId, resourceType, resourceId, resourceName,
}: InfraContextPanelProps) {
  const [opened, setOpened] = useState(true)

  const { data, isLoading, error } = useQuery<{ context: InfraContext }>({
    queryKey: ['chamado-context', tenantId, taskRequestId],
    queryFn: async () => {
      const res = await api.get<{ context: InfraContext }>(
        `/v1/tenants/${tenantId}/workflow/${taskRequestId}`
      )
      return res.data
    },
    enabled: !!tenantId && !!taskRequestId,
    retry: false,
    staleTime: 30_000,
  })

  const context = data?.context

  return (
    <Box>
      <Group
        onClick={() => setOpened((o) => !o)}
        style={{ cursor: 'pointer' }}
        mb="xs"
        justify="space-between"
      >
        <Group gap="xs">
          <IconServer size={14} />
          <Text size="sm" fw={500}>Contexto de Infraestrutura</Text>
          {resourceName && (
            <Badge size="xs" variant="outline">{resourceType?.toUpperCase()}: {resourceName}</Badge>
          )}
        </Group>
        <ActionIcon variant="subtle" size="xs">
          {opened ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
        </ActionIcon>
      </Group>

      <Collapse in={opened}>
        {isLoading && <Skeleton height={60} />}

        {error && (
          <Text size="xs" c="dimmed">Contexto indisponível.</Text>
        )}

        {context && (
          <Stack gap="xs">
            {/* Alertas ativos */}
            {context.active_alerts.length > 0 && (
              <>
                <Text size="xs" fw={500} c="dimmed">Alertas Ativos</Text>
                {context.active_alerts.map((a, i) => (
                  <Alert key={i} color="orange" variant="light" icon={<IconAlertCircle size={12} />} py={4}>
                    <Text size="xs">{a.message}</Text>
                  </Alert>
                ))}
                <Divider />
              </>
            )}

            {/* Estado do recurso */}
            {context.resource_state && resourceId && (
              <>
                <Text size="xs" fw={500} c="dimmed">Estado do Recurso</Text>
                <Group gap="md">
                  {context.resource_state.status && (
                    <Box>
                      <Text size="xs" c="dimmed">Status</Text>
                      <Badge
                        size="xs"
                        color={context.resource_state.status === 'running' ? 'green' : 'red'}
                        variant="light"
                      >
                        {context.resource_state.status}
                      </Badge>
                    </Box>
                  )}
                  {context.resource_state.cpu_pct !== undefined && (
                    <Box>
                      <Text size="xs" c="dimmed">CPU</Text>
                      <Text size="xs" fw={500}>{context.resource_state.cpu_pct?.toFixed(1)}%</Text>
                    </Box>
                  )}
                  {context.resource_state.disk_pct !== undefined && (
                    <Box>
                      <Text size="xs" c="dimmed">Disco</Text>
                      <Text
                        size="xs"
                        fw={500}
                        c={context.resource_state.disk_pct > 85 ? 'red' : undefined}
                      >
                        {context.resource_state.disk_pct?.toFixed(1)}%
                      </Text>
                    </Box>
                  )}
                </Group>
                <Divider />
              </>
            )}

            {/* Eventos recentes */}
            {context.recent_audit_events.length > 0 && (
              <>
                <Text size="xs" fw={500} c="dimmed">Atividade Recente do Tenant (24h)</Text>
                <Stack gap={2}>
                  {context.recent_audit_events.slice(0, 5).map((ev, i) => (
                    <Group key={i} gap="xs">
                      <Badge size="xs" variant="dot" color="gray">{ev.action}</Badge>
                      <Text size="xs" c="dimmed">{ev.actor}</Text>
                      <Text size="xs" c="dimmed" ml="auto">
                        {new Date(ev.at).toLocaleTimeString('pt-BR')}
                      </Text>
                    </Group>
                  ))}
                </Stack>
              </>
            )}

            {!resourceId && context.active_alerts.length === 0 && context.recent_audit_events.length === 0 && (
              <Text size="xs" c="dimmed">Nenhum dado de contexto disponível.</Text>
            )}
          </Stack>
        )}
      </Collapse>
    </Box>
  )
}
